import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import { LoadingSpinner } from '../../../shared/ui';
import {
  AdminRelatedLinks,
  AdminBlogShell,
  AdminPageHeader,
  usePreservedDomainSearch,
} from './AdminBlogListPage';
import { appendYowanPreviewQueryIfNeeded } from '../../../app/host/yowanPreviewRouting';
import { AdminFacilityEditorFields } from './AdminFacilitiesCreatePage';
import {
  fetchYowanAdminFacilityDetail,
  getYowanAdminMutationErrorMessage,
  updateYowanAdminFacility,
} from '../app/yowanContentClient';
const { yowanPageContent } = require('./pageContent');

const AdminFacilitiesEditPage = () => {
  const content = yowanPageContent['/admin/events/facilities/:facilityId/edit'];
  const navigate = useNavigate();
  const preservedDomainSearch = usePreservedDomainSearch();
  const { facilityId } = useParams();
  const [currentRecord, setCurrentRecord] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [submitMessage, setSubmitMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [roomIndexes, setRoomIndexes] = useState([0]);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues:
      currentRecord
        ? { ...currentRecord, image: '' }
        : {
            facilityName: '',
            shortName: '',
            address: '',
            phone: '',
            gmap: '',
            url: '',
            image: '',
            isActive: true,
            remarks: '',
            rooms: [
              {
                roomName: '',
                capacity: '',
                isActive: true,
                remarks: '',
              },
            ],
        },
    shouldUnregister: true,
  });

  useEffect(() => {
    if (!content || !facilityId) {
      return undefined;
    }

    let isMounted = true;

    const loadFacility = async () => {
      setIsLoading(true);
      setSubmitMessage('');

      try {
        const record = await fetchYowanAdminFacilityDetail(facilityId);

        if (!isMounted) {
          return;
        }

        const rooms = record.rooms?.length
          ? record.rooms
          : [{ roomName: '', capacity: '', isActive: true, remarks: '' }];

        setCurrentRecord(record);
        reset({ ...record, rooms, image: '' });
        setRoomIndexes(Array.from({ length: rooms.length }, (_, index) => index));
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setCurrentRecord(null);
        setSubmitMessage('施設を読み込めませんでした。');
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadFacility();

    return () => {
      isMounted = false;
    };
  }, [content, facilityId, reset]);

  if (!content) {
    return null;
  }

  const onSubmit = async (values) => {
    setIsSubmitting(true);
    setSubmitMessage(content.form.pendingMessage);

    try {
      await updateYowanAdminFacility(facilityId, values);
      setSubmitMessage('保存しました。');
      navigate(appendYowanPreviewQueryIfNeeded('/admin/events/facilities', preservedDomainSearch));
    } catch (error) {
      setSubmitMessage(getYowanAdminMutationErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleAddRoom = () => {
    setRoomIndexes((currentIndexes) => [
      ...currentIndexes,
      currentIndexes.length ? Math.max(...currentIndexes) + 1 : 0,
    ]);
  };
  const handleRemoveRoom = (roomIndex) => {
    setRoomIndexes((currentIndexes) => {
      if (currentIndexes.length <= 1) {
        return currentIndexes;
      }

      return currentIndexes.filter((currentIndex) => currentIndex !== roomIndex);
    });
  };

  return (
    <AdminBlogShell activeNavHref="/admin/events/facilities" routeKey="/admin/events/facilities/:facilityId/edit">
      <AdminPageHeader
        title={content.header.title}
        paragraphs={content.header.paragraphs}
        actions={[
          <Link key="back-to-list" to={appendYowanPreviewQueryIfNeeded('/admin/events/facilities', preservedDomainSearch)}>
            一覧へ戻る
          </Link>,
        ]}
      />
      <AdminRelatedLinks links={content.relatedLinks} preservedDomainSearch={preservedDomainSearch} />

      {isLoading || !currentRecord ? (
        <div className="yowan-admin-form c-surface-content">
          {isLoading ? <LoadingSpinner size="sm" /> : submitMessage || '施設が見つかりませんでした。'}
        </div>
      ) : (
        <form className="yowan-admin-form c-surface-content" onSubmit={handleSubmit(onSubmit)}>
          <AdminFacilityEditorFields
            content={content}
            register={register}
            errors={errors}
            roomIndexes={roomIndexes}
            onAddRoom={handleAddRoom}
            onRemoveRoom={handleRemoveRoom}
            currentImage={currentRecord?.primary_image}
          />

          <div className="yowan-admin-form__actions">
            <button type="submit" className="c-form__submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <LoadingSpinner size="sm" />
                  {content.form.pendingMessage}
                </>
              ) : content.form.submitLabel}
            </button>
          </div>

          {submitMessage ? <p className="yowan-admin-form__message">{submitMessage}</p> : null}
        </form>
      )}
    </AdminBlogShell>
  );
};

export default AdminFacilitiesEditPage;
